// TODO: migrate from legacy CI tooling: resolve ts-* and/or eslint-* directives below (including inline comments)

import type { Layer } from "@deck.gl/core";
import _ from "lodash";
import Tooltip from "rc-tooltip";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import { Button, Icon, Input } from "semantic-ui-react";
import Histogram from "../../../../Lumber/Histogram";
import {
	BlueYellowLayer,
	ClampedLayer,
	GreenFireBlueLayer,
	RYGLayer,
	SpectralLayer,
	ThermalLayer,
} from "../../../../Lumber/OrthoViewer/orthoShaders";
import type { ColorStop } from "../MapStyles";
import { useViewer, useViewerDispatch } from "../StateManagement";
import { DraggableWindow } from "./InternalComponents/DraggableWindow";
import { GradientButton } from "./InternalComponents/GradientButton";
import { TransparencySlider } from "./InternalComponents/TransparencySlider";

type Props = {
	histogramBuckets: number[];
};

export const OrthoOptions = ({ histogramBuckets }: Props) => {
	const [popupOpen, setPopupOpen] = useState(false);

	return (
		<>
			<Button className="actionsPopupTrigger iconButton" icon onClick={() => setPopupOpen((prev) => !prev)}>
				<Tooltip
					placement="top"
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					motion={{ motionName: "rc-tooltip-zoom" }}
					overlay="Options"
				>
					<Icon className="cog" size="large" style={{ width: 21, height: 21 }} />
				</Tooltip>
			</Button>
			{createPortal(
				<OrthoOptionsPopup histogramBuckets={histogramBuckets} open={popupOpen} setOpen={setPopupOpen} />,
				document.body,
			)}
		</>
	);
};

type PopupProps = {
	histogramBuckets: number[];
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

const OrthoOptionsPopup = ({ histogramBuckets, open, setOpen }: PopupProps) => {
	const INIT_AUTO_THRESH = 5000;
	const GRADIENTS: { [key: string]: ColorStop[] } = {
		by: [
			{ color: "#0000ff", stop: 0 },
			{ color: "#9d02d7", stop: 0.17 },
			{ color: "#cd34b5", stop: 0.33 },
			{ color: "#ea5f94", stop: 0.5 },
			{ color: "#fa8775", stop: 0.67 },
			{ color: "#ffb14e", stop: 0.83 },
			{ color: "#ffd700", stop: 1 },
		],
		gfb: [
			{ color: "#000000", stop: 0 },
			{ color: "#00a9ff", stop: 0.25 },
			{ color: "#00ff00", stop: 0.5 },
			{ color: "#ffff00", stop: 0.75 },
			{ color: "#ffffff", stop: 1 },
		],
		ryg: [
			{ color: "#ff0000", stop: 0 },
			{ color: "#ffff00", stop: 0.5 },
			{ color: "#00ff00", stop: 1 },
		],
		spectral: [
			{ color: "#660000", stop: 0 },
			{ color: "#ff0000", stop: 0.12 },
			{ color: "#ff8800", stop: 0.25 },
			{ color: "#ffff00", stop: 0.5 },
			{ color: "#00ff00", stop: 0.68 },
			{ color: "#00ffff", stop: 0.85 },
			{ color: "#0000ff", stop: 1 },
		],
		thermal: [
			{ color: "#000000", stop: 0 },
			{ color: "#4b0082", stop: 0.25 },
			{ color: "#ff0000", stop: 0.5 },
			{ color: "#ffff00", stop: 0.75 },
			{ color: "#ffffff", stop: 1 },
		],
		clamped: [
			{ color: "#000000", stop: 0 },
			{ color: "#ffffff", stop: 1 },
		],
		none: [
			{ color: "#000000", stop: 0 },
			{ color: "#ffffff", stop: 1 },
		],
	};
	const shaderLayers = useMemo(
		() => ({
			by: BlueYellowLayer,
			gfb: GreenFireBlueLayer,
			ryg: RYGLayer,
			spectral: SpectralLayer,
			thermal: ThermalLayer,
			clamped: ClampedLayer,
		}),
		[],
	);

	const { viewerMapboxUploads, layerOrder, orthoVisible } = useViewer();
	const { updateDeckLayers } = useViewerDispatch();

	const [minClamp, setMinClamp] = useState(0.0);
	const [maxClamp, setMaxClamp] = useState(1.0);
	const [minInput, setMinInput] = useState(0);
	const [maxInput, setMaxInput] = useState(1);
	const [minInputErr, setMinInputErr] = useState(false);
	const [maxInputErr, setMaxInputErr] = useState(false);
	const [autoThresh, setAutoThresh] = useState(INIT_AUTO_THRESH);
	const [activeShader, setActiveShader] = useState<"by" | "gfb" | "ryg" | "spectral" | "thermal" | "clamped" | "none">(
		"none",
	);
	const [imageOpacity, setImageOpacity] = useState(1);

	const autoAdjust = useCallback(() => {
		let autoMin = 0;
		let autoMax = 0;
		const size = _.reduce(histogramBuckets, (sum, a) => sum + a, 0);
		setAutoThresh(autoThresh < 10 ? INIT_AUTO_THRESH : autoThresh / 2);
		const lim = size / 10;
		const thresh = size / autoThresh;

		let count = 0;
		let i = 0;
		while (i < 255 && count <= thresh) {
			i++;
			count = histogramBuckets[i];
			if (count > lim) {
				count = 0;
			}
		}
		autoMin = i;

		count = 0;
		i = 255;
		while (i > 0 && count <= thresh) {
			i--;
			count = histogramBuckets[i];
			if (count > lim) {
				count = 0;
			}
		}
		autoMax = i;

		if (autoMax < autoMin) {
			autoMin = 0;
			autoMax = 255;
			setAutoThresh(INIT_AUTO_THRESH);
		}

		return [autoMin, autoMax];
	}, [histogramBuckets, autoThresh]);

	const applyClamp = useCallback(
		(min: number, max: number) => {
			const validateThresholds = () => {
				let valid = true;

				const isNumeric = (n: string | number): n is number => {
					return !isNaN(parseFloat(n as string)) && isFinite(n as number);
				};

				if (!isNumeric(min) || !isNumeric(max)) {
					toast.error("Min and max thresholds must be numeric");
					return false;
				}

				if (min < 0 || min > 1) {
					toast.error("Min threshold must be between 0 and 1");
					valid = false;
				}

				if (max < 0 || max > 1) {
					toast.error("Max threshold must be between 0 and 1");
					valid = false;
				}

				if (min > max) {
					toast.error("Max threshold must be higher than min threshold");
					valid = false;
				}

				return valid;
			};

			if (validateThresholds() && activeShader !== "none") {
				const layers: Layer[] = _.map(
					viewerMapboxUploads,
					(vmu) =>
						new shaderLayers[activeShader]({
							data: {
								tileUrl: `${process.env.MAPBOX_TILE_URL}${vmu.tilesetId}/{z}/{x}/{y}.@2x.png?access_token=${process.env.MAPBOX_ACCESS_TOKEN}`,
								min: min,
								max: max,
								layerId: `shader-${activeShader}-tile-${vmu.tilesetId}`,
								inverse: false,
								beforeId: `z-${2 - layerOrder.indexOf("ortho")}`,
								opacity: orthoVisible ? imageOpacity : 0,
							},
						}),
				);

				updateDeckLayers(layers);
			} else {
				updateDeckLayers([]);
			}
		},
		[activeShader, viewerMapboxUploads, updateDeckLayers, shaderLayers, layerOrder, orthoVisible, imageOpacity],
	);

	useEffect(() => {
		applyClamp(minClamp, maxClamp);
	}, [applyClamp, minClamp, maxClamp]);

	const resetAdjust = () => {
		setAutoThresh(INIT_AUTO_THRESH);
		setMinClamp(0.0);
		setMaxClamp(1.0);
		setMinInput(0);
		setMaxInput(1);
	};

	// const handleSet = () => {
	// 	// if (dropdownRef.current) {
	// 		// setHistogramBuckets(dropdownRef.current.innerRef.current.getImageHistogram()); // only works for 8-bit color
	// 	// }

	// 	const max = getMaxThreshold();
	// 	const min = getMinThreshold();

	// 	setMinClamp(min);
	// 	setMinInput(min);
	// 	setMaxClamp(max);
	// 	setMaxInput(max);
	// 	setMinInputErr(false);
	// 	setMaxInputErr(false);
	// 	if (min === 0 && max === 1) setAutoThresh(initAutoThresh);
	// };

	return open ? (
		<DraggableWindow
			zId="ortho"
			title="Imagery Options"
			open={open}
			setOpen={setOpen}
			rootStyle={{ width: 450 }}
			contentClassName="imageryOptionsModal"
		>
			<Histogram
				skipLow
				skipHigh
				buckets={histogramBuckets}
				slider
				sliderValues={[minClamp * 255, maxClamp * 255]}
				onSliderUpdate={(v) => {
					setMinClamp(v[0] / 255);
					setMaxClamp(v[1] / 255);
					setMinInput(Math.round((v[0] / 255) * 100) / 100);
					setMaxInput(Math.round((v[1] / 255) * 100) / 100);
					setMinInputErr(false);
					setMaxInputErr(false);
				}}
				rootStyle={{ height: histogramBuckets.length > 0 ? 150 : "", width: "auto", gridArea: "hist" }}
				colorscale={GRADIENTS[activeShader]?.map(({ stop, color }) => [stop, color])}
			/>
			<TransparencySlider
				rootStyle={{ gridArea: "opacity" }}
				currentOpacity={imageOpacity}
				onChange={(opacity) => {
					const rounded = Math.round(opacity * 100) / 100;
					setImageOpacity(rounded);
				}}
			/>

			<Input
				size="mini"
				style={{ width: 50, gridArea: "min" }}
				type="text"
				value={minInput}
				error={minInputErr}
				onChange={(e) => {
					setMinInput(Number(e.target.value));
					let v: number | null = null;
					if (e.target.value) {
						v = Number(e.target.value);
						if (isNaN(v) || v > 1 || v < 0) {
							setMinInputErr(true);
						} else {
							setMinInputErr(false);
							setMinClamp(v);
						}
					}
				}}
			/>

			<Input
				size="mini"
				style={{ width: 50, gridArea: "max", justifySelf: "end" }}
				type="text"
				value={maxInput}
				error={maxInputErr}
				onChange={(e) => {
					setMaxInput(Number(e.target.value));
					let v: number | null = null;
					if (e.target.value) {
						v = Number(e.target.value);
						if (isNaN(v) || v > 1 || v < 0) {
							setMaxInputErr(true);
						} else {
							setMaxInputErr(false);
							setMaxClamp(v);
						}
					}
				}}
			/>
			<div style={{ display: "inline-flex", alignItems: "center", gridArea: "adjust" }}>
				<Button
					color="blue"
					compact
					floated="left"
					content="Auto"
					disabled={histogramBuckets.length === 0}
					onClick={() => {
						const [autoMin, autoMax] = autoAdjust();
						setMinClamp(autoMin / 255);
						setMaxClamp(autoMax / 255);
						setMinInput(Math.round((autoMin / 255) * 100) / 100);
						setMaxInput(Math.round((autoMax / 255) * 100) / 100);
					}}
				/>
				{histogramBuckets.length > 0 && (
					<Tooltip
						placement="right"
						align={{ offset: [-3, 0] }}
						mouseEnterDelay={0}
						mouseLeaveDelay={0}
						trigger="hover"
						overlay={
							<>
								<p style={{ textAlign: "center" }}>
									Automatically adjusts the histogram to improve contrast
									<br />
									Repeated presses will further adjust the histogram
								</p>
							</>
						}
					>
						<Icon circular inverted name="info" size="tiny" link />
					</Tooltip>
				)}
			</div>
			<Button
				style={{ gridArea: "reset" }}
				negative
				compact
				floated="right"
				content="Reset"
				onClick={() => {
					resetAdjust();
				}}
			/>
			<div className="buttonContainer">
				<Tooltip
					placement="right"
					align={{ offset: [0, -2] }}
					mouseEnterDelay={0}
					mouseLeaveDelay={0}
					trigger="hover"
					overlay={
						<>
							<p>Original Image</p>
						</>
					}
				>
					<Button
						className="gradientButton"
						style={{
							outline: activeShader === "none" ? "" : "none",
							background:
								"repeating-linear-gradient(45deg, rgba(0,0,0,0), rgba(0,0,0,0) 8%, rgba(0,0,0,1) 8%, rgba(0,0,0,1) 16%)",
						}}
						onClick={() => {
							// setMinThreshold(0);
							// setMaxThreshold(1);
							setActiveShader("none");
						}}
					/>
				</Tooltip>
				<GradientButton
					selected={activeShader === "clamped"}
					onClick={() => {
						setActiveShader("clamped");
					}}
					title="Grayscale"
					colorStops={GRADIENTS["clamped"]}
				/>
				<GradientButton
					selected={activeShader === "ryg"}
					onClick={() => {
						setActiveShader("ryg");
					}}
					title="Red-Yellow-Green"
					colorStops={GRADIENTS["ryg"]}
				/>
				<GradientButton
					selected={activeShader === "thermal"}
					onClick={() => {
						setActiveShader("thermal");
					}}
					title="Thermal"
					colorStops={GRADIENTS["thermal"]}
				/>
				<GradientButton
					selected={activeShader === "gfb"}
					onClick={() => {
						setActiveShader("gfb");
					}}
					title="Green-Fire-Blue"
					colorStops={GRADIENTS["gfb"]}
				/>
				<GradientButton
					selected={activeShader === "spectral"}
					onClick={() => {
						setActiveShader("spectral");
					}}
					title="Spectral"
					colorStops={GRADIENTS["spectral"]}
				/>
				<GradientButton
					selected={activeShader === "by"}
					onClick={() => {
						setActiveShader("by");
					}}
					title="Blue-Yellow"
					colorStops={GRADIENTS["by"]}
				/>
			</div>
		</DraggableWindow>
	) : (
		<></>
	);
};
